import { useEffect, useRef, useState } from 'react'
import LogoIcon from '@/features/shared/components/Logo/logo.svg?react'
import { cn } from '@/lib/utils'

interface BlogPostCoverProps {
  title: string
  coverImage: string | null
  className?: string
}

/**
 * 16:9 cover. Posts without an image — or whose external image fails to
 * load — get a dot-grid plate with the logo mark, so the listing grid stays
 * uniform without a gradient.
 */
export function BlogPostCover({
  title,
  coverImage,
  className,
}: BlogPostCoverProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(coverImage) && !failed

  // An SSR-rendered image can fail before React attaches onError; catch
  // that case once on mount.
  useEffect(() => {
    const img = imgRef.current
    if (img?.complete && img.naturalWidth === 0) setFailed(true)
  }, [])

  return (
    <div
      className={cn(
        'relative aspect-video overflow-hidden bg-[var(--color-bg-tertiary)]',
        className,
      )}
    >
      {showImage ? (
        <img
          ref={imgRef}
          src={coverImage ?? undefined}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 ease-brisk group-hover:scale-[1.04]"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <>
          {/* Brand wash behind the dot grid, so image-less posts still carry
              the gradient language instead of reading as an empty plate. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-accent/15"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(var(--color-border-strong) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              maskImage:
                'radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 100%)',
            }}
          />
          <div
            role="img"
            aria-label={title}
            className="relative flex h-full items-center justify-center"
          >
            <LogoIcon className="h-12 w-12 text-primary opacity-70 transition-transform duration-500 ease-brisk group-hover:scale-110" />
          </div>
        </>
      )}
    </div>
  )
}
